import { createSlice } from "@reduxjs/toolkit";

import { getStocks } from "../actions/stocks/getStocks";
import { getStock } from "../actions/stocks/getStock";
import { getStockPriceHistory } from "../actions/stocks/getStockPriceHistory";
import { getStockOrders } from "../actions/stocks/getStockOrders";
import { createStock } from "../actions/stocks/createStock";
import { updateStock } from "../actions/stocks/updateStock";
import { updateStockPrice } from "../actions/stocks/updateStockPrice";
import { configurePurification } from "../actions/stocks/configurePurification";
import { toggleStockActive } from "../actions/stocks/toggleStockActive";
import { approveOrder } from "../actions/stocks/approveOrder";
import { rejectOrder } from "../actions/stocks/rejectOrder";
import { deleteStock } from "../actions/stocks/deleteStock";
import { placeOrderForUser } from "../actions/stocks/placeOrderForUser";
import { sellOrderForUser } from "../actions/stocks/sellOrderForUser";
import { downloadStockTemplate } from "../actions/stocks/downloadStockTemplate";
import { bulkUploadStocks } from "../actions/stocks/bulkUploadStocks";

export const stocksSlice = createSlice({
  name: "stocks",
  initialState: {
    error: null,
    allStocks: [],
    allStocksInfo: null,
    stock: null,
    priceHistory: [],
    priceHistoryInfo: null,
    buyOrders: [],
    buyOrdersInfo: null,
    sellOrders: [],
    sellOrdersInfo: null,
    getStocksPending: false,
    getStockPending: false,
    priceHistoryPending: false,
    buyOrdersPending: false,
    sellOrdersPending: false,
    createPending: false,
    updatePending: false,
    updatePricePending: false,
    configurePurificationPending: false,
    togglePending: false,
    approvePending: false,
    rejectPending: false,
    deletePending: false,
    placeForUserPending: false,
    sellForUserPending: false,
    downloadTemplatePending: false,
    bulkUploadPending: false,
  },

  reducers: {},

  extraReducers: (builder) => {
    // List stocks
    builder.addCase(getStocks.pending, (state) => {
      state.getStocksPending = true;
    });
    builder.addCase(getStocks.fulfilled, (state, action) => {
      state.getStocksPending = false;
      state.allStocksInfo = action.payload;
      state.allStocks = action.payload?.stocks || action.payload?.data || [];
    });
    builder.addCase(getStocks.rejected, (state, action) => {
      state.getStocksPending = false;
      state.error = action?.error?.message;
    });

    // Single stock
    builder.addCase(getStock.pending, (state) => {
      state.getStockPending = true;
      state.stock = null;
    });
    builder.addCase(getStock.fulfilled, (state, action) => {
      state.getStockPending = false;
      state.stock = action.payload;
    });
    builder.addCase(getStock.rejected, (state, action) => {
      state.getStockPending = false;
      state.error = action?.error?.message;
    });

    // Price history
    builder.addCase(getStockPriceHistory.pending, (state) => {
      state.priceHistoryPending = true;
    });
    builder.addCase(getStockPriceHistory.fulfilled, (state, action) => {
      state.priceHistoryPending = false;
      state.priceHistory = action.payload?.history || [];
      state.priceHistoryInfo = action.payload?.meta || null;
    });
    builder.addCase(getStockPriceHistory.rejected, (state, action) => {
      state.priceHistoryPending = false;
      state.error = action?.error?.message;
    });

    // Orders (buy / sell tabs)
    builder.addCase(getStockOrders.pending, (state, action) => {
      const side = action.meta.arg?.side || "buy";
      if (side === "sell") state.sellOrdersPending = true;
      else state.buyOrdersPending = true;
    });
    builder.addCase(getStockOrders.fulfilled, (state, action) => {
      const side = action.meta.arg?.side || "buy";
      if (side === "sell") {
        state.sellOrdersPending = false;
        state.sellOrders = action.payload?.orders || [];
        state.sellOrdersInfo = action.payload?.meta || null;
      } else {
        state.buyOrdersPending = false;
        state.buyOrders = action.payload?.orders || [];
        state.buyOrdersInfo = action.payload?.meta || null;
      }
    });
    builder.addCase(getStockOrders.rejected, (state, action) => {
      const side = action.meta.arg?.side || "buy";
      if (side === "sell") state.sellOrdersPending = false;
      else state.buyOrdersPending = false;
      state.error = action?.error?.message;
    });

    // Create
    builder.addCase(createStock.pending, (state) => {
      state.createPending = true;
    });
    builder.addCase(createStock.fulfilled, (state) => {
      state.createPending = false;
    });
    builder.addCase(createStock.rejected, (state, action) => {
      state.createPending = false;
      state.error = action?.error?.message;
    });

    // Update
    builder.addCase(updateStock.pending, (state) => {
      state.updatePending = true;
    });
    builder.addCase(updateStock.fulfilled, (state) => {
      state.updatePending = false;
    });
    builder.addCase(updateStock.rejected, (state, action) => {
      state.updatePending = false;
      state.error = action?.error?.message;
    });

    // Update price
    builder.addCase(updateStockPrice.pending, (state) => {
      state.updatePricePending = true;
    });
    builder.addCase(updateStockPrice.fulfilled, (state) => {
      state.updatePricePending = false;
    });
    builder.addCase(updateStockPrice.rejected, (state, action) => {
      state.updatePricePending = false;
      state.error = action?.error?.message;
    });

    // Configure purification
    builder.addCase(configurePurification.pending, (state) => {
      state.configurePurificationPending = true;
    });
    builder.addCase(configurePurification.fulfilled, (state) => {
      state.configurePurificationPending = false;
    });
    builder.addCase(configurePurification.rejected, (state, action) => {
      state.configurePurificationPending = false;
      state.error = action?.error?.message;
    });

    // Toggle active
    builder.addCase(toggleStockActive.pending, (state) => {
      state.togglePending = true;
    });
    builder.addCase(toggleStockActive.fulfilled, (state) => {
      state.togglePending = false;
    });
    builder.addCase(toggleStockActive.rejected, (state, action) => {
      state.togglePending = false;
      state.error = action?.error?.message;
    });

    // Approve order
    builder.addCase(approveOrder.pending, (state) => {
      state.approvePending = true;
    });
    builder.addCase(approveOrder.fulfilled, (state) => {
      state.approvePending = false;
    });
    builder.addCase(approveOrder.rejected, (state, action) => {
      state.approvePending = false;
      state.error = action?.error?.message;
    });

    // Reject order
    builder.addCase(rejectOrder.pending, (state) => {
      state.rejectPending = true;
    });
    builder.addCase(rejectOrder.fulfilled, (state) => {
      state.rejectPending = false;
    });
    builder.addCase(rejectOrder.rejected, (state, action) => {
      state.rejectPending = false;
      state.error = action?.error?.message;
    });

    // Delete stock
    builder.addCase(deleteStock.pending, (state) => {
      state.deletePending = true;
    });
    builder.addCase(deleteStock.fulfilled, (state) => {
      state.deletePending = false;
    });
    builder.addCase(deleteStock.rejected, (state, action) => {
      state.deletePending = false;
      state.error = action?.error?.message;
    });

    // Place order for user
    builder.addCase(placeOrderForUser.pending, (state) => {
      state.placeForUserPending = true;
    });
    builder.addCase(placeOrderForUser.fulfilled, (state) => {
      state.placeForUserPending = false;
    });
    builder.addCase(placeOrderForUser.rejected, (state, action) => {
      state.placeForUserPending = false;
      state.error = action?.error?.message;
    });

    // Sell order for user
    builder.addCase(sellOrderForUser.pending, (state) => {
      state.sellForUserPending = true;
    });
    builder.addCase(sellOrderForUser.fulfilled, (state) => {
      state.sellForUserPending = false;
    });
    builder.addCase(sellOrderForUser.rejected, (state, action) => {
      state.sellForUserPending = false;
      state.error = action?.error?.message;
    });

    // Download bulk-upload template
    builder.addCase(downloadStockTemplate.pending, (state) => {
      state.downloadTemplatePending = true;
    });
    builder.addCase(downloadStockTemplate.fulfilled, (state) => {
      state.downloadTemplatePending = false;
    });
    builder.addCase(downloadStockTemplate.rejected, (state, action) => {
      state.downloadTemplatePending = false;
      state.error = action?.error?.message;
    });

    // Bulk upload stocks
    builder.addCase(bulkUploadStocks.pending, (state) => {
      state.bulkUploadPending = true;
    });
    builder.addCase(bulkUploadStocks.fulfilled, (state) => {
      state.bulkUploadPending = false;
    });
    builder.addCase(bulkUploadStocks.rejected, (state, action) => {
      state.bulkUploadPending = false;
      state.error = action?.error?.message;
    });
  },
});

export default stocksSlice.reducer;
